// Insurance Scope financial model — single source of truth.
// Consumed by:
//   - ScopeSummaryCard + ScopeEditorModal + AgreementReviewModal (staff UI)
//   - installAgreementPDF (customer-facing agreement document)
//
// Spec: memory/features/insurance-scope-redesign.md (Apr 24, 2026)
//
// Apr 27, 2026 correction (per Greg): `dep_credit` only reduces the depreciation
// we collect from insurance — it does NOT act as a credit against the
// deductible. The homeowner never receives the depreciation cash (insurance
// only releases it on completion), so there's nothing to offset their
// deductible with. Only `acv_credit` (cash the HO got from the insurance
// ACV check that we're not collecting) and `misc_credit` (goodwill / referral)
// can offset the deductible.
//
// May 5, 2026 correction (per Greg): PWI is NOT withheld from the ACV check.
// Insurance companies keep PWI entirely separate — the ACV check is simply
// ACV − Deductible. PWI is held by the carrier and released with the
// depreciation settlement after invoicing.
//
// May 7, 2026 correction (per Greg): ACV Credit is NOT subtracted from the ACV
// settlement to Bardin. It only offsets the deductible (homeowner side). The
// previous model double-counted it (subtracted from ACV side AND credited the
// deductible), under-collecting by acv_credit. New model: HO endorses the FULL
// ACV check to Bardin (ACV − Deductible), and the un-performed ACV value
// shows up only as a credit reducing the homeowner's out-of-pocket.
//
// May 15, 2026 — Non-Collect Revenue redesign (per Greg):
//   • `non_commissionable` is renamed "Non-Collect Revenue" in the UI (column
//     name kept on disk to avoid a 14-file sweep). It is still subtracted from
//     commissionable revenue. It does NOT affect the initial invoice amount.
//   • When non-collect revenue > $500, the OVERAGE (amount above $500) is
//     further deducted from RM + TL commission at Chad's approval step,
//     split 75% RM / 25% TL by default. Chad can edit the split per-deal.
//     If no TL is on the deal, 100% of the overage falls on RM.
//   • The legacy "Misc Credit ≥ $501 → ⅓ RM / ⅓ ASM / ⅓ company" rule is
//     VOIDED. Misc Credit is no longer in the commissionable-reduction +
//     penalty path; it's just a goodwill-toward-deductible value owned by
//     Shannon at post-build time.
//   • commissionable = initial_invoice − at_cost_retail − misc_credit
//                      − non_commissionable − punch_list_rm_cost
//     (formula unchanged in shape — non_commissionable still subtracts.
//      The new piece is `nonCollectOverage` exposed below, which downstream
//      consumers (CommissionBoard) apply against RM/TL totals.)
//
// Split insurance side into two settlements that mirror how the carrier pays:
//   ACV Settlement           = ACV − Deductible              ← no PWI subtraction
//   ACV Settlement to Bardin = ACV Settlement                ← acv_credit NOT subtracted (May 7)
//   First Payment to Bardin  = ACV Settlement to Bardin + HO Out-of-Pocket
//   Depreciation Settlement  = (Depreciation − DEP Credit) + Approved Supplements + PWI
//   Insurance Pays           = ACV Settlement to Bardin + Depreciation Settlement
//
// `acv_only` flag: when true, insurance is only paying ACV. Depreciation
// Settlement is forced to 0 (no dep, no supplements, no PWI release) and the
// total `insurance_pays` collapses to just the ACV settlement to Bardin.
//
// Formulas:
//   acv_settlement       = acv − deductible                            ← PWI NOT subtracted
//   acv_settlement_bardin = acv_settlement                             ← acv_credit only credits deductible (May 7)
//   ho_gross             = deductible + upgrades + retail_profit + at_cost_retail
//   deductible_credits   = acv_credit + misc_credit          ← dep_credit excluded
//   credits_applied      = min(deductible_credits, deductible)   ← capped
//   credit_overage       = max(0, deductible_credits − deductible)
//   ho_out_of_pocket     = ho_gross − credits_applied
//   first_payment        = acv_settlement_bardin + ho_out_of_pocket
//   dep_settlement       = acv_only ? 0 : max(0, dep − dep_credit) + supplements + pwi
//                          (supplements = APPROVED only — a pending request is 0, Sep 15 2026)
//   insurance_pays       = acv_settlement_bardin + dep_settlement
//                        = (acv_only ? acv − deductible
//                                    : rcv − deductible − dep_credit + supplements + pwi)
//   initial_invoice      = insurance_pays + ho_gross − deductible_credits
//                        = work-value performed by Bardin
//                        = (acv_only ? acv − acv_credit + upgrades + retail_profit + at_cost_retail
//                                    : rcv − acv_credit − dep_credit + supplements + pwi
//                                       + upgrades + retail_profit + at_cost_retail)
//                          − misc_credit
//   commissionable       = initial_invoice − supplements − at_cost_retail
//                          − non_commissionable − punch_list_rm_cost
//                          (Jun 30, 2026 per Greg: supplements excluded from
//                           commission; they stay in initial_invoice/collections.
//                           supplements term is 0 on acv_only deals.)
//
// Oct 29, 2026 — trade money (Greg: "the homeowner agreement picks it up from
// there"; "let it blend into non-collect revenue"; "if the customer is charged
// above the 30% floor, all that works just like retail commissions"). SQL twins
// in migration 20261029f_trade_money.sql (fn_deal_initial_invoice,
// fn_deal_invoice_gross, fn_deal_commissionable; fn_deal_first_payment is
// deliberately unchanged).
//   A homeowner-billed trade (gutters etc. scheduled through a sub from the
//   trade sheet) carries its customer price onto the deal as derived columns
//   that SQL trade_sync_deal keeps from trade_item_prices × trade_items:
//   trade_ho_total (Σ customer price of the live homeowner-billed trades),
//   trade_ho_floor (Σ min(price, the 30% floor = sub cost / (1 − target))),
//   trade_margin_loss (Σ the shortfalls Chad deducted, whatever the payer) and
//   trade_ho_lines (the customer-facing lines). Insurance- and Bardin-billed
//   trades never enter these sums. The trade is DUE AT COMPLETION, never in
//   the first payment.
//   initial_invoice      = … + trade_ho_total            (insurance and retail)
//   first_payment        = unchanged (hoGross / hoOutOfPocket / retail deposit)
//   completion_payment   = max(0, initial_invoice − first_payment)
//                        = dep_settlement + trade_ho_total − credit_overage
//                          (retail: the other half of the contract + trade)
//   commissionable       = … − trade_ho_total             (insurance: the floor
//                          is at cost, the upside is a pool the Commission
//                          board pays as its own line type, trade_upside;
//                          the trade entered initial_invoice, so the rate
//                          base does not move)
//   trade_upside         = max(0, trade_ho_total − trade_ho_floor), recomputed
//                          here from the two sums (never the third column)
//   retail_pool          = max(0, contract − base − at-cost + trade_upside)
//                          (retail: the upside joins the pool; the contract
//                          total stays the roof contract)
//   non_collect_overage  = max(0, non_commissionable + trade_margin_loss − 500)
//                          (retail: max(0, trade_margin_loss − 500)) — ONE
//                          bucket per deal; the first $500 is forgiven. The
//                          ≥$501 credits rule (rate tier, pay hold) is
//                          unchanged: it still counts misc_credit +
//                          non_commissionable only.

import type { Deal, PunchListItem, TradeHoLine } from "./types";

export interface ScopeCalc {
  rcv: number;
  dep: number;
  acv: number;
  deductible: number;
  /** Effective supplement amount used in calcs — APPROVED ONLY (Sep 15, 2026).
   *  Equals supplement_approved_amount if set (final); 0 while a request is
   *  outstanding (supplement_invoice_amount > 0, nothing approved yet); the
   *  legacy `supplements` column only when neither lifecycle column is in play.
   *  Mirrors public.fn_deal_invoice_gross. A pending request is signaled by
   *  `supplementsPending` + `supplementInvoiceAmount` so surfaces can mention
   *  it without ever adding it to a total. */
  supplements: number;
  /** What Bardin invoiced the carrier (raw column). */
  supplementInvoiceAmount: number;
  /** What the carrier approved (raw column). */
  supplementApprovedAmount: number;
  /** True when an invoice amount is set but the approved amount isn't yet —
   *  HO Payment Schedule shows a "waiting on insurance final approval" flag
   *  while in this state. Apr 30, 2026. */
  supplementsPending: boolean;
  pwi: number;
  upgrades: number;
  retailProfit: number;
  atCostRetail: number;
  acvCredit: number;
  depCredit: number;
  miscCredit: number;
  /** UI-labeled "Non-Collect Revenue". Column name stays `non_commissionable`. */
  nonCommissionable: number;
  /** Alias for nonCommissionable with the new semantic name. */
  nonCollectRevenue: number;
  /** The deal's one deduction bucket (Oct 29, 2026, per Greg: "let it blend into
   *  non-collect revenue"): max(0, nonCollectRevenue + tradeMarginLoss − 500) on
   *  an insurance deal, max(0, tradeMarginLoss − 500) on retail. The first $500
   *  of the bucket is forgiven; what is over comes off the RM / TL / ASM
   *  commission at Chad's approve step (the May 15, 2026 split). Whether the
   *  non-collect part applies at all (non_collect_deduct_active, grandfathered
   *  deals) is the Commission board's call, not this function's. */
  nonCollectOverage: number;
  // ── Trade money (Oct 29, 2026) — homeowner-billed trades from the trade sheet ──
  /** Σ customer price of the live, homeowner-billed trades (deals.trade_ho_total,
   *  derived by SQL trade_sync_deal). In initialInvoice; due at completion;
   *  never in firstPaymentToBardin. */
  tradeHoTotal: number;
  /** Σ over the same trades of min(customer price, the price at the 30% floor =
   *  sub cost / (1 − margin target)). At cost to the commission formula. */
  tradeHoFloor: number;
  /** max(0, tradeHoTotal − tradeHoFloor): the pool paid as commission (the RM by
   *  default; Chad can share it with the TL / ASM). Insurance: a trade_upside
   *  line on the board. Retail: already inside retailPool. Recomputed from the
   *  two sums, never read from a third column. */
  tradeUpside: number;
  /** Σ the trade price shortfalls Chad deducted (deals.trade_margin_loss). Blends
   *  into nonCollectOverage; it is NOT in commissionable. */
  tradeMarginLoss: number;
  /** The customer-facing lines (label, amount) for the agreement and the
   *  invoice, in the order the trades were made. Never a sub figure. */
  tradeHoLines: TradeHoLine[];
  /** What is due after the build, the final balance before anything past the
   *  first payment is received: max(0, initialInvoice − firstPaymentToBardin).
   *  On an insurance deal that is depSettlement + tradeHoTotal − creditOverage
   *  (the carrier's depreciation check plus the trades the homeowner owes at
   *  completion, less any credit beyond the deductible); on retail it is
   *  retailCompletionPayment (the other half of the contract + the trades).
   *  It is the number the homeowner's portal shows after the build once the
   *  first payment is in (the stored invoice − what was paid). The invoice is
   *  what the homeowner owes in full: non-collect revenue is Bardin's internal
   *  stance and is not netted here (PaymentScheduleCard, Jun 19, 2026). */
  completionPayment: number;
  /** When true, insurance is only paying ACV — no depreciation recoverable. */
  acvOnly: boolean;
  // Insurance side — split into two settlements
  /** ACV − Deductible. Gross ACV check from insurance, payable to HO. PWI is NOT part of this check — the carrier holds it separately and releases it with the dep settlement. */
  acvSettlement: number;
  /** Equal to acvSettlement (May 7, 2026): HO endorses the full ACV − Deductible to Bardin. acv_credit only offsets the deductible, not this number. */
  acvSettlementToBardin: number;
  /** ACV Settlement to Bardin + HO Out-of-Pocket. The first wave of cash to Bardin (HO endorses + pays before build). */
  firstPaymentToBardin: number;
  /** (Depreciation − DEP Credit) + Approved Supplements + PWI. Released after completion. 0 if acv_only. */
  depSettlement: number;
  /** Total dollars insurance pays Bardin (across both settlements). */
  insurancePays: number;
  hoGross: number;
  /** Sum of credits eligible to offset the deductible (acv + misc). dep_credit excluded. */
  deductibleCredits: number;
  /** @deprecated Use deductibleCredits. Kept for backward compat — same value as deductibleCredits. */
  totalCredits: number;
  creditsApplied: number;
  creditOverage: number;
  hoOutOfPocket: number;
  initialInvoice: number;
  punchListRmCost: number;
  commissionable: number;
  // ── Retail mode (Jun 11, 2026) ───────────────────────────────────────────
  /** True when the deal was converted to a full retail job (insurance denied /
   *  partial approval). All insurance-side numbers above are overridden:
   *  firstPaymentToBardin = 50% deposit, depSettlement = the completion
   *  payment (the other 50% + the trades billed to the homeowner, Oct 29,
   *  2026), insurancePays = 0, initialInvoice = contract total + those trades,
   *  and commissionable = retailPool − punchListRmCost. */
  isRetail: boolean;
  /** Fixed retail contract total (the roof contract; the homeowner-billed
   *  trades from the trade sheet are billed on top of it, Oct 29, 2026). */
  retailContractTotal: number;
  /** Squares entered by the RM (pre-fill from EagleView, editable). */
  retailSquares: number;
  /** $/sq base snapshot taken at conversion (finance_settings.retail_base_per_square, default 460). */
  retailBasePerSquare: number;
  /** squares × base — Bardin's base full-roof-replacement price for this job. */
  retailBaseTotal: number;
  /** Sum of at-cost retail line items (kind='at_cost'). No commission on these —
   *  they wash out of the pool but are still billed in the contract total. */
  retailAtCostTotal: number;
  /** max(0, contract total − base total − at-cost total + tradeUpside). RM/TL/ASM
   *  commission pool; split set per job by Chad. At-cost line items earn no
   *  commission; the trades' price above their 30% floor joins the pool
   *  (Oct 29, 2026: "all that works just like retail commissions"). */
  retailPool: number;
  /** 50% of contract total — due before build. The trade is not in it (Oct 29, 2026). */
  retailDeposit: number;
  /** Remaining 50% + the homeowner-billed trades — due upon completion. */
  retailCompletionPayment: number;
}

/** One customer-facing trade line on the deal (deals.trade_ho_lines). */
export type { TradeHoLine };

export function calcScope(deal: Partial<Deal> | Record<string, unknown>): ScopeCalc {
  const n = (k: string): number => {
    const v = (deal as Record<string, unknown>)[k];
    return typeof v === "number" ? v : Number(v) || 0;
  };
  const b = (k: string): boolean => {
    const v = (deal as Record<string, unknown>)[k];
    return v === true;
  };

  const rcv = n("rcv_total");
  const dep = n("depreciation_total");
  const acv = rcv - dep;
  const deductible = n("deductible");
  // Apr 30, 2026 — supplement state has two phases:
  //   1. Pending: Bardin invoiced the carrier (supplement_invoice_amount > 0)
  //      but the carrier hasn't approved yet (supplement_approved_amount = 0).
  //   2. Final: Carrier approved (supplement_approved_amount > 0).
  //
  // Sep 15, 2026 (Greg, via Shannon): a PENDING request contributes NOTHING to
  // the math. It used to ride into depSettlement → initialInvoice as the full
  // requested amount, which inflated the homeowner's Payment Schedule by the
  // whole ask (carriers approve about half on average) — and because that
  // field was frequently typed as Apex's entire rebuilt RCV rather than the
  // added amount, by more than the original roof (Nemetz showed $52.7K against
  // a real $20.8K). Every charging path (fn_deal_invoice_gross →
  // final_invoice_amount, balances, payment links) already treated pending as
  // 0; the client now agrees.
  // Effective amount = approved if set → 0 while a request is outstanding →
  // legacy `supplements` column only when neither lifecycle column is set.
  // `supplementsPending` + `supplementInvoiceAmount` stay exposed so a surface
  // can MENTION the outstanding request.
  const supplementInvoiceAmount = n("supplement_invoice_amount");
  const supplementApprovedAmount = n("supplement_approved_amount");
  const supplementsLegacy = n("supplements");
  const supplements =
    supplementApprovedAmount > 0
      ? supplementApprovedAmount
      : supplementInvoiceAmount > 0
        ? 0
        : supplementsLegacy;
  const supplementsPending = supplementApprovedAmount <= 0 && supplementInvoiceAmount > 0;
  const pwi = n("pwi_code");
  const upgrades = n("upgrades");
  const retailProfit = n("retail_profit");
  const atCostRetail = n("at_cost_retail");
  const acvCredit = n("acv_credit");
  const depCredit = n("dep_credit");
  const miscCredit = n("misc_credit");
  const nonCommissionable = n("non_commissionable");
  // Oct 29, 2026 — trade money. The sums are derived columns (SQL trade_sync_deal
  // recomputes them from trade_item_prices × trade_items on every trade write);
  // the upside is recomputed here from the two sums so a stale third column can
  // never pay a pool the floor does not support.
  const tradeHoTotal = n("trade_ho_total");
  const tradeHoFloor = n("trade_ho_floor");
  const tradeUpside = Math.max(0, tradeHoTotal - tradeHoFloor);
  const tradeMarginLoss = n("trade_margin_loss");
  const tradeHoLinesRaw = (deal as Record<string, unknown>).trade_ho_lines;
  const tradeHoLines: TradeHoLine[] = Array.isArray(tradeHoLinesRaw)
    ? (tradeHoLinesRaw as Array<Record<string, unknown>>)
        .filter((l) => l !== null && typeof l === "object")
        .map((l) => ({
          trade_id: String(l.trade_id ?? ""),
          label: String(l.label ?? ""),
          amount: Number(l.amount) || 0,
        }))
    : [];
  // May 15, 2026 — surface the over-$500 overage as a derived field so
  // downstream consumers (CommissionBoard) can compute the RM/TL deduction
  // without re-deriving the threshold themselves. Single source of truth.
  // Oct 29, 2026 (Greg: "If they have not used the $500 and this is $300 below
  // 30%, it deducts from there") — the trade shortfalls Chad deducted blend
  // into the same bucket; the $500 is forgiven once, over the whole bucket.
  const NON_COLLECT_THRESHOLD = 500;
  const nonCollectOverage = Math.max(0, nonCommissionable + tradeMarginLoss - NON_COLLECT_THRESHOLD);
  const acvOnly = b("acv_only");

  // Insurance side — two settlements.
  // PWI ("Paid When Invoiced") is held separately by the carrier and released
  // with the depreciation settlement after invoice. It is NOT part of the ACV
  // check — the carrier keeps it off the ACV side entirely.
  // May 7, 2026: acv_credit is NO LONGER subtracted here. It only offsets the
  // deductible (deductibleCredits below). Subtracting it on both sides was a
  // double-discount that under-collected by acvCredit on every claim.
  const acvSettlement = acv - deductible;
  const acvSettlementToBardin = acvSettlement;
  const depSettlement = acvOnly ? 0 : Math.max(0, dep - depCredit) + supplements + pwi;

  // Homeowner side
  const hoGross = deductible + upgrades + retailProfit + atCostRetail;
  // Apr 27, 2026: dep_credit only reduces what we collect from insurance —
  // it does NOT offset the deductible (HO never receives dep cash).
  const deductibleCredits = acvCredit + miscCredit;
  const creditsApplied = Math.min(deductibleCredits, deductible);
  const creditOverage = Math.max(0, deductibleCredits - deductible);
  const hoOutOfPocket = hoGross - creditsApplied;

  // First payment to Bardin (paid by HO before build) = insurance ACV portion + HO out-of-pocket
  const firstPaymentToBardin = acvSettlementToBardin + hoOutOfPocket;

  // Total insurance pays Bardin across both settlements
  const insurancePays = acvSettlementToBardin + depSettlement;

  // initial_invoice = the net amount Bardin actually invoices and collects. It
  // MUST equal firstPaymentToBardin + depSettlement (the two amounts the
  // homeowner sees on the Payment Schedule card), so every credit applied to the
  // homeowner — acv_credit AND misc_credit — is netted out here. (Since Oct 29,
  // 2026 the trades billed to the homeowner ride on top, due at completion:
  // initial_invoice = firstPaymentToBardin + completionPayment.)
  //
  // History:
  //   - May 7, 2026: stopped double-subtracting acv_credit (it had been removed
  //     from both the insurance side and the credits side).
  //   - Jun 8, 2026: misc_credit is now subtracted here too. Previously it was
  //     kept "gross" in initial_invoice and only removed once in `commissionable`
  //     below, which made the customer-facing Initial Invoice Total overstate by
  //     misc_credit — First Payment already had it netted out, so First Payment +
  //     Depreciation came up short of the displayed total by exactly misc_credit.
  //     Now it's removed here, and the `- miscCredit` term is dropped from
  //     `commissionable` below; the two changes cancel, so commission payouts are
  //     unchanged.
  //
  // Note on non-collect revenue (May 15, 2026): non_commissionable does NOT
  // reduce the initial_invoice. The carrier + homeowner are billed the full
  // contracted amount; Bardin simply does not pursue collections on the
  // non-collect portion. This is acknowledged by the HO at install-agreement
  // signing (deals.non_collect_acknowledged). Commission impact lives below.
  //
  // Oct 29, 2026: + the homeowner-billed trades from the trade sheet. They are
  // billed on this invoice and due at completion — NOT in hoGross, so
  // hoOutOfPocket and firstPaymentToBardin (and the first-payment hold and the
  // build payment-plan gate that read SQL fn_deal_first_payment) never move.
  // ⚠ Mirrored in SQL fn_deal_initial_invoice / fn_deal_invoice_gross.
  const initialInvoice = insurancePays + hoGross - acvCredit - miscCredit + tradeHoTotal;
  // What the carrier and the homeowner still owe after the first payment: the
  // depreciation check plus the trades, less any credit beyond the deductible.
  const completionPayment = Math.max(0, initialInvoice - firstPaymentToBardin);

  const punchListItems = Array.isArray((deal as Record<string, unknown>).punch_list_items)
    ? ((deal as Record<string, unknown>).punch_list_items as PunchListItem[])
    : [];
  const punchListRmCost = punchListItems
    .filter((i) => i.responsibility === "rm" && i.cost != null)
    .reduce((s, i) => s + (i.cost ?? 0), 0);

  // misc_credit is already netted out of initialInvoice above (Jun 8, 2026), so
  // it is NOT subtracted again here — doing so would double-count it.
  //
  // Jun 30, 2026 (per Greg): supplements are NOT commissionable. They remain in
  // initialInvoice / final_invoice / collections (real money Bardin bills and
  // collects from the carrier), but commission is computed off the base scope
  // only. Subtract the supplement contribution back out of the commission basis
  // here — it entered via depSettlement → insurancePays → initialInvoice. On
  // acv_only deals depSettlement is 0 so supplements were never in initialInvoice;
  // commissionSupplements mirrors that so we never over-subtract.
  // ⚠ Mirrored in SQL public.fn_deal_commissionable — if this changes, change both.
  // Note: the SQL pair (fn_deal_initial_invoice → fn_deal_commissionable) still
  // resolves a pending request to the requested amount, but it enters and is
  // subtracted by the same term, so commissionable is identical whether the
  // supplement is pending, approved, or (as here) zeroed while pending.
  const commissionSupplements = acvOnly ? 0 : supplements;
  // Oct 29, 2026: the homeowner-billed trade entered initialInvoice above; the
  // WHOLE of it leaves the rate formula here — its floor is at cost (like
  // at_cost_retail) and its upside is the pool the Commission board pays as a
  // trade_upside line. Paying the rate on it as well would pay the upside twice.
  const commissionable =
    initialInvoice - commissionSupplements - atCostRetail - tradeHoTotal - nonCommissionable - punchListRmCost;

  // ── Retail mode (Jun 11, 2026, per Greg) ─────────────────────────────────
  // Full retail job after an insurance denial / partial approval. Fixed
  // contract total, 50% deposit before build / 50% upon completion.
  // Commission pool = contract total − (squares × $460/sq base, snapshot at
  // conversion) − at-cost line items; RM/TL/ASM split is set per job by Chad on
  // the Commission board. The insurance columns may still hold the old claim
  // numbers for history — they are ignored here.
  //
  // Jun 17, 2026 — the contract is now built from pickable line items
  // (deals.retail_items): one roof line (squares × base apply to it) plus other
  // items each 'normal' (fully commissionable) or 'at_cost' (no commission).
  // Since the roof + normal lines are fully in the pool and at-cost lines are
  // not, the pool reduces to contract − base − at-cost (the at-cost amount is in
  // the contract total but excluded from the pool).
  //
  // ⚠ Mirrored in SQL: public.fn_deal_commissionable (retail branch). If this
  // math changes, change both.
  const isRetail = b("is_retail");
  const retailContractTotal = n("retail_contract_total");
  const retailSquares = n("retail_squares");
  const retailBasePerSquareRaw = n("retail_base_per_square");
  const retailBasePerSquare = retailBasePerSquareRaw > 0 ? retailBasePerSquareRaw : 460;
  const retailBaseTotal = retailSquares * retailBasePerSquare;
  // Jun 17, 2026 — at-cost line items earn no commission. Prefer the
  // denormalized column; fall back to summing retail_items when only the jsonb
  // is in scope (e.g. a select that fetched items but not the numeric column).
  const retailItemsRaw = (deal as Record<string, unknown>).retail_items;
  const retailItemsAtCost = Array.isArray(retailItemsRaw)
    ? (retailItemsRaw as Array<Record<string, unknown>>)
        .filter((it) => it && it.kind === "at_cost")
        .reduce((s, it) => s + (Number(it.amount) || 0), 0)
    : 0;
  const retailAtCostCol = n("retail_at_cost_total");
  const retailAtCostTotal = retailAtCostCol > 0 ? retailAtCostCol : retailItemsAtCost;
  // Oct 29, 2026 (Greg: "all that works just like retail commissions") — the
  // trade's upside joins the pool. The contract total stays the roof contract;
  // the trade is a separate amount due at completion (the deposit is unchanged).
  const retailPool = Math.max(0, retailContractTotal - retailBaseTotal - retailAtCostTotal + tradeUpside);
  const retailDeposit = retailContractTotal / 2;
  const retailCompletionPayment = retailContractTotal - retailDeposit + tradeHoTotal;
  // ⚠ Mirrored in SQL fn_deal_initial_invoice / fn_deal_invoice_gross (retail
  // branch: contract + trade_ho_total); fn_deal_first_payment keeps contract / 2.
  const retailInitialInvoice = retailContractTotal + tradeHoTotal;

  const retailFields = {
    isRetail,
    retailContractTotal,
    retailSquares,
    retailBasePerSquare,
    retailBaseTotal,
    retailAtCostTotal,
    retailPool,
    retailDeposit,
    retailCompletionPayment,
  };

  if (isRetail) {
    return {
      rcv, dep, acv, deductible, supplements, pwi, upgrades, retailProfit, atCostRetail,
      acvCredit, depCredit, miscCredit, nonCommissionable,
      // Non-collect revenue is an insurance-claim concept — zeroed on retail so
      // PDF boxes / signing checkboxes never fire. The deduction bucket still
      // carries the trade shortfalls Chad deducted (Oct 29, 2026), first $500
      // forgiven.
      nonCollectRevenue: 0,
      nonCollectOverage: Math.max(0, tradeMarginLoss - NON_COLLECT_THRESHOLD),
      tradeHoTotal, tradeHoFloor, tradeUpside, tradeMarginLoss, tradeHoLines,
      completionPayment: retailCompletionPayment,
      acvOnly,
      supplementInvoiceAmount, supplementApprovedAmount,
      supplementsPending: false,
      acvSettlement: 0,
      acvSettlementToBardin: 0,
      // Generic payment surfaces (PaymentPortal, StatusPortal,
      // notifyHomeownerPaymentDue) read firstPaymentToBardin as "due before
      // build" and initialInvoice as the full invoice — mapping the 50/50
      // schedule onto them keeps every downstream balance computation correct
      // without per-surface branching.
      firstPaymentToBardin: retailDeposit,
      depSettlement: retailCompletionPayment,
      insurancePays: 0,
      // Everything on a retail job is homeowner money: the roof contract and
      // the trades billed to the homeowner (Oct 29, 2026).
      hoGross: retailInitialInvoice,
      deductibleCredits: 0,
      totalCredits: 0,
      creditsApplied: 0,
      creditOverage: 0,
      hoOutOfPocket: retailDeposit,
      initialInvoice: retailInitialInvoice,
      punchListRmCost,
      commissionable: retailPool - punchListRmCost,
      ...retailFields,
    };
  }

  return {
    rcv, dep, acv, deductible, supplements, pwi, upgrades, retailProfit, atCostRetail,
    acvCredit, depCredit, miscCredit, nonCommissionable,
    nonCollectRevenue: nonCommissionable,
    nonCollectOverage,
    tradeHoTotal, tradeHoFloor, tradeUpside, tradeMarginLoss, tradeHoLines,
    completionPayment,
    acvOnly,
    supplementInvoiceAmount, supplementApprovedAmount, supplementsPending,
    acvSettlement, acvSettlementToBardin, firstPaymentToBardin, depSettlement,
    insurancePays, hoGross,
    deductibleCredits,
    totalCredits: deductibleCredits, // legacy alias — same value
    creditsApplied, creditOverage, hoOutOfPocket,
    initialInvoice, punchListRmCost, commissionable,
    ...retailFields,
  };
}

// ---------------------------------------------------------------------------
// Non-collect overage commission deduction (May 15, 2026)
//
// Given an overage amount (= the deal's bucket over $500: nonCollectOverage,
// which since Oct 29, 2026 blends the non-collect revenue with the trade
// shortfalls Chad deducted) and the per-deal RM split percent, return the
// dollar deduction applied to each party. ASM override and RS pool are NOT impacted. TL share rolls onto RM
// (100% RM) when no TL is on the deal.
// ---------------------------------------------------------------------------

export interface NonCollectDeduction {
  /** Total dollars deducted from commissions (= overage). */
  total: number;
  /** Dollars deducted from RM commission. */
  rm: number;
  /** Dollars deducted from TL override. 0 when no TL on the deal. */
  tl: number;
  /** Effective RM split percent used in the calc (after no-TL fallback). */
  effectiveRmPct: number;
}

export function calcNonCollectDeduction(
  overage: number,
  rmSharePct: number,        // 0..1, default 0.75
  hasTl: boolean,
): NonCollectDeduction {
  const safeOverage = Math.max(0, Number(overage) || 0);
  if (safeOverage <= 0) {
    return { total: 0, rm: 0, tl: 0, effectiveRmPct: hasTl ? rmSharePct : 1 };
  }
  // No-TL fallback per Greg (May 15, 2026): TL share collapses to 100% RM
  // when no TL is on the deal.
  const effectiveRmPct = hasTl ? Math.min(1, Math.max(0, rmSharePct)) : 1;
  const rm = safeOverage * effectiveRmPct;
  const tl = safeOverage - rm;
  return { total: safeOverage, rm, tl, effectiveRmPct };
}
